import * as React from "react";
import { createRoot } from "react-dom/client";
import { HashRouter } from "react-router-dom";
import App from "./components/App";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import "./taskpane.css";
import { Auth0Provider } from '@auth0/auth0-react';
import { authConfig } from "./config/auth-config";
import { AnalysisGuardProvider } from "./hooks/useAnalysisGuard";

/* global document, Office, module, require, HTMLElement */

const title = "Spreetail Sales Intelligence";

const rootElement: HTMLElement | null = document.getElementById("container");
const root = rootElement ? createRoot(rootElement) : undefined;


// Global guard to prevent multiple initialization
if (typeof window !== 'undefined') {
  (window as any).__taskpaneInitialized = (window as any).__taskpaneInitialized || false;
}

// Render function
const renderApp = (AppComponent: React.ComponentType<{ title: string }> = App) => {
  if (!root) return;
  
  root.render(
    <FluentProvider theme={webLightTheme} targetDocument={document}>
      <Auth0Provider
        domain={authConfig.domain}
        clientId={authConfig.clientId}
        authorizationParams={{
          redirect_uri: authConfig.redirectUri,
          audience: authConfig.audience,
          scope: authConfig.scope,
        }}
      >
        <AnalysisGuardProvider>
          <HashRouter>
            <AppComponent title={title} />
          </HashRouter>
        </AnalysisGuardProvider>
      </Auth0Provider>
    </FluentProvider>
  );
};

/* Render application after Office initializes */
if (typeof window !== 'undefined' && !(window as any).__taskpaneInitialized) {
  Office.onReady(() => {
    if ((window as any).__taskpaneInitialized) {
      console.warn("Office.onReady called multiple times, skipping duplicate render");
      return;
    }
    
    console.log("Office onReady");
    (window as any).__taskpaneInitialized = true;
    renderApp();
  });
} else {
  console.warn("Taskpane already initialized, skipping Office.onReady registration");
}

// Handle HMR - only re-render if already initialized
if ((module as any).hot) {
  (module as any).hot.accept("./components/App", () => {
    if (!(window as any).__taskpaneInitialized || !root) {
      console.warn("HMR: App not yet initialized, skipping");
      return;
    }
    
    const NextApp = require("./components/App").default;
    renderApp(NextApp);
  });

}